import { ButtonColor } from "@/common/enum/button-color";
import { Permission } from "@/common/enum/permission";
import { departmentService } from "@/services/department-service";
import { DepartmentDto } from "@/types/dto/department-dto";
import { Button, Modal, Select, TextInput, Radio, Label } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { toastState } from "../../atoms/system/toast-state";
import { adminPageState } from "../../atoms/user/admin-page-state";
import { userService } from "../../services/user-service";
import { CreateAdminPayload } from "../../types/payload/create-admin-payload";
import { CustomHelperText } from "../CustomHelperText";
import { GrayCheckCircleIcon } from "../icon/GrayCheckCircleIcon";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Regular } from "../typography/Body14Regular";
import { Body14Strong } from "../typography/Body14Strong";
import { Body16Regular } from "../typography/Body16Regular";
import { H4 } from "../typography/H4";

const initPayload = {
  emailName: "",
  host: "aqara.kr",
  name: "",
  departmentId: 0,
  departmentName: "",
  permission: Permission.READ,
};

interface Props {
  closeModal: () => void;
}

export const PlatformAdminModal: React.FC<Props> = ({ closeModal }) => {
  const [complete, setComplete] = useState(false);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);

  const initPassword = "aqaralife2024@";
  const loginUrl = window.location.origin + "/login";

  const setPage = useSetRecoilState(adminPageState);
  const setToast = useSetRecoilState(toastState);

  const [inputs, setInputs] = useState(initPayload);

  const [aggregateErrors, setAggregateErrors] = useState({
    emailName: "",
    name: "",
    departmentId: "",
  });

  const getDepartment = async () => {
    const response = await departmentService.getPage({ page: 1, size: 100 });
    setDepartments(response.data.contents);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleDropDownClick = (id: number, name: string) => {
    setInputs({ ...inputs, departmentId: id, departmentName: name });
  };

  const handlePermissionChange = (permission: Permission) => {
    setInputs({ ...inputs, permission });
  };

  const handleSubmit = async () => {
    const payload: CreateAdminPayload = {
      email: `${inputs.emailName}@${inputs.host}`,
      name: inputs.name,
      departmentId: inputs.departmentId,
      permission: inputs.permission,
    };
    const createAdminResponse = await userService.createAdmin(payload);
    setAggregateErrors({
      emailName: createAdminResponse.data.aggregateErrors?.email || "",
      name: createAdminResponse.data.aggregateErrors?.name || "",
      departmentId: "",
    });
    if (createAdminResponse.ok) {
      const getAdminByIdResponse = await userService.getAdminById(
        createAdminResponse.data.id
      );
      if (!getAdminByIdResponse.ok) {
        return;
      }
      setPage((prev) => ({
        ...prev,
        contents: [getAdminByIdResponse.data, ...prev.contents],
        totalElements: prev.totalElements + 1,
      }));
      setComplete(true);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(
      `아이디(이메일): ${inputs.emailName}@${inputs.host}\n비밀번호: ${initPassword}\n로그인 링크: ${loginUrl}`
    );
    setToast({
      show: true,
      messages: ["내용을 복사했습니다."],
    });
  };

  useEffect(() => {
    setComplete(false);
    getDepartment();
  }, []);

  const disabled = !inputs.emailName || !inputs.name || !inputs.departmentId;

  return (
    <Modal size="2xl" show={true} onClose={closeModal}>
      <Modal.Header className="p-[24px] h-[76px]">
        <H4>관리자 추가</H4>
      </Modal.Header>
      <Modal.Body>
        {!complete && (
          <div className="flex justify-center">
            <div className="flex flex-col gap-[20px]">
              <div className="flex flex-col">
                <TextInputLabel required>이메일 (아이디)</TextInputLabel>
                <div className="flex flex-col">
                  <div className="flex items-center gap-[8px]">
                    <TextInput
                      required
                      value={inputs.emailName}
                      placeholder="아이디"
                      name="emailName"
                      onChange={handleTextInputChange}
                      className="w-[271.5px]"
                    />
                    <Body14Regular>@</Body14Regular>
                    <TextInput
                      value={inputs.host}
                      disabled={true}
                      className="w-[271.5px] bg-grayscale-contrast-2"
                    />
                  </div>
                  {aggregateErrors.emailName && (
                    <CustomHelperText
                      isError={!!aggregateErrors.emailName}
                      text={aggregateErrors.emailName}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-col">
                <TextInputLabel required>이름</TextInputLabel>
                <div className="flex items-center">
                  <TextInput
                    required
                    value={inputs.name}
                    placeholder="이름을 입력해주세요"
                    name="name"
                    onChange={handleTextInputChange}
                    className="w-[572px]"
                  />
                </div>
                {aggregateErrors.name && (
                  <CustomHelperText
                    isError={!!aggregateErrors.name}
                    text={aggregateErrors.name}
                  />
                )}
              </div>
              <div className="flex flex-col">
                <TextInputLabel required>부서</TextInputLabel>
                <Select
                  className="w-[572px]"
                  color="black"
                  onChange={(e) => {
                    const selectedDepartment = departments.find(
                      (dept) => dept.name === e.target.value
                    );
                    if (!selectedDepartment) {
                      handleDropDownClick(0, "");
                      return;
                    }
                    handleDropDownClick(
                      selectedDepartment.id,
                      selectedDepartment.name
                    );
                  }}
                >
                  <option key={0}>부서를 선택해주세요.</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.name}>
                      {department.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex flex-col">
                <TextInputLabel required>권한</TextInputLabel>
                <fieldset className="flex max-w-md gap-4">
                  <div className="flex items-center gap-2">
                    <Radio
                      id="permission-read"
                      name="permission"
                      value="READ"
                      checked={inputs.permission === Permission.READ}
                      onChange={() => handlePermissionChange(Permission.READ)}
                    />
                    <Label htmlFor="permission-read">읽기 전용</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Radio
                      id="permission-write"
                      name="permission"
                      value="WRITE"
                      checked={inputs.permission === Permission.WRITE}
                      onChange={() => handlePermissionChange(Permission.WRITE)}
                    />
                    <Label htmlFor="permission-write">편집</Label>
                  </div>
                </fieldset>
              </div>
              <div>
                <Body14Regular className="text-semantic-text-secondary">
                  비밀번호는 자동생성됩니다.
                </Body14Regular>
              </div>
            </div>
          </div>
        )}
        {complete && (
          <div className="flex flex-col pb-[1px] items-center gap-[20px]">
            <div>
              <GrayCheckCircleIcon size={31.5} />
            </div>
            <div>
              <Body16Regular className="text-semantic-text-body">
                관리자 계정 추가가 완료되었습니다.
              </Body16Regular>
            </div>
            <div className="w-[572px] h-[156px] flex flex-col gap-[12px] bg-grayscale-contrast-2 rounded-[8px] px-[12px] py-[16px] border border-semantic-divider">
              <div>
                <Body14Regular className="text-semantic-text-body">
                  마스터 계정 생성이 완료되었습니다. 보안을 위해 비밀번호를
                  변경해주세요.
                </Body14Regular>
              </div>
              <div className="flex gap-[12px]">
                <div>
                  <Body14Strong className="text-semantic-text-body">
                    이메일(아이디)
                  </Body14Strong>
                </div>
                <div>
                  <Body14Regular className="text-semantic-text-body">
                    {`${inputs.emailName}@${inputs.host}` ||
                      "testman@gmail.com"}
                  </Body14Regular>
                </div>
              </div>
              <div className="flex gap-[12px]">
                <div>
                  <Body14Strong className="text-semantic-text-body">
                    비밀번호
                  </Body14Strong>
                </div>
                <div>
                  <Body14Regular className="text-semantic-text-body">
                    {initPassword}
                  </Body14Regular>
                </div>
              </div>
              <div className="flex gap-[12px]">
                <div>
                  <Body14Strong className="text-semantic-text-body">
                    로그인 링크
                  </Body14Strong>
                </div>
                <div>
                  <Body14Regular className="text-semantic-text-body">
                    {loginUrl}
                  </Body14Regular>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!complete && (
          <Button
            disabled={disabled}
            color={disabled ? ButtonColor.DISABLED : ButtonColor.PRIMARY}
            onClick={handleSubmit}
          >
            추가하기
          </Button>
        )}

        {complete && (
          <Button color={ButtonColor.PRIMARY} onClick={copyToClipboard}>
            계정 정보 복사하기
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
